import * as Sentry from '@sentry/react';

const SHADOWCAM_DIAG = '[BLUE-STREAM-12010:SessionCast]';

// Default capture resolution for matchmaking-v2 per-session ShadowCam capture. The host requesting an
// exact size drives Playjector to render the instance's virtual monitor at exactly this resolution (see
// the capture-resolution note in api/stores/shared.ts); 1280x720 is a ShadowCam DirectShow catalog size,
// so `exact` negotiates cleanly. Per-campaign overrides can replace this constant later.
export const DEFAULT_CAPTURE_RESOLUTION = { width: 1280, height: 720 } as const;

export const SHADOWCAM_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 2,
  autoGainControl: false,
  noiseSuppression: false,
  echoCancellation: false,
};

/** True when A+V failed in a way that often clears by dropping audio and keeping video. */
const isAvFallbackError = (error: unknown): boolean => {
  const name = (error as DOMException)?.name;
  return name === 'OverconstrainedError' || name === 'NotReadableError';
};

/**
 * Open ShadowCam via getUserMedia. Prefer exact resolution; on A+V
 * Overconstrained/NotReadable retry video-only so cast can keep picture and
 * fall back to AudioCaster-by-label for sound.
 */
export async function getUserMediaForShadowCam(
  sessionId: string,
  hubRoute: string,
  target: { deviceId: string; displayName: string },
  withAudio: boolean,
  diag: { availableLabels: string[]; pluggedSlots: string[] },
): Promise<MediaStream> {
  const { width, height } = DEFAULT_CAPTURE_RESOLUTION;
  const videoExact: MediaTrackConstraints = {
    deviceId: { exact: target.deviceId },
    width: { exact: width },
    height: { exact: height },
  };
  const videoIdeal: MediaTrackConstraints = {
    deviceId: { exact: target.deviceId },
    width: { ideal: width },
    height: { ideal: height },
  };
  // Prefer `audio: true` so Chromium can bind the ShadowCam composite audio pin
  // to the same getUserMedia as the video deviceId (Phase 5 A+V path).
  const audioConstraint: boolean | MediaTrackConstraints = withAudio ? true : false;

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('navigator.mediaDevices.getUserMedia is unavailable');
  }

  try {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: videoExact,
        audio: audioConstraint,
      });
    } catch (firstError) {
      // Prefer A+V; if audio constraints cause Overconstrained/NotReadable,
      // retry video-only so cast can still fall back to AudioCaster by label.
      let constraintError: unknown = firstError;
      if (isAvFallbackError(constraintError) && withAudio) {
        Sentry.logger.warn(
          `${SHADOWCAM_DIAG} ${target.displayName} A+V failed (${(constraintError as DOMException)?.name}); ` +
          `retrying video-only`,
        );
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: videoExact,
            audio: false,
          });
        } catch (videoOnlyError) {
          if (!isAvFallbackError(videoOnlyError)) {
            throw videoOnlyError;
          }
          constraintError = videoOnlyError;
        }
      }
      if ((constraintError as DOMException)?.name === 'OverconstrainedError') {
        Sentry.logger.warn(
          `${SHADOWCAM_DIAG} ${target.displayName} cannot open at exact ${width}x${height}; retrying with ideal`,
        );
        return await navigator.mediaDevices.getUserMedia({
          video: videoIdeal,
          audio: audioConstraint,
        });
      }
      throw constraintError;
    }
  } catch (error) {
    console.error(
      `${SHADOWCAM_DIAG} getUserMedia THREW session=${sessionId} hubRoute=${hubRoute} ` +
      `device="${target.displayName}":`,
      error,
    );
    Sentry.captureException(error, {
      tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'get_user_media' },
      fingerprint: ['shadowcam-getusermedia-throw', hubRoute],
      extra: {
        sessionId,
        hubRoute,
        resolvedDevice: target.displayName,
        enumeratedShadowcams: diag.availableLabels,
        pluggedSlots: diag.pluggedSlots,
        withAudio,
      },
    });
    throw error;
  }
}
